import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DistrictCreate from '../../create/js/districtCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { useApp } from '../../../../context/AppContext';
import { districtAPI } from '../../../../utils/api';
import PropTypes from 'prop-types';

function DistrictTable({ onCreateDistrict, reloadKey = 0 }) {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));
  const canCreate = permissions.includes('CREATE_DISTRICT') || isSuperAdmin;
  const canEdit = permissions.includes('EDIT_DISTRICT') || isSuperAdmin;
  const canDelete = permissions.includes('DELETE_DISTRICT') || isSuperAdmin;

  const fetchDistricts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await districtAPI.getAll();
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts, reloadKey]);

  const handleView = district => {
    setSelectedDistrict(district);
    setShowViewModal(true);
  };

  const handleEdit = district => {
    setSelectedDistrict(district);
    setShowEditModal(true);
  };

  const handleDelete = async (district) => {
    const result = await Swal.fire({
      title: 'Delete District?',
      text: `Are you sure you want to delete ${district.name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await districtAPI.delete(district.id);
        await fetchDistricts();
        Swal.fire('Deleted!', 'District has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.message || 'Failed to delete district', 'error');
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'district_id', header: 'Code', size: 100 },
    { accessorKey: 'name', header: 'District Name', size: 200 },
    { accessorKey: 'taluka_names', header: 'Talukas', size: 300 },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => handleView(row.original)}><FaEye /></button>
          {canEdit && <button className="eduss-global-action-btn edit" onClick={() => handleEdit(row.original)}><FaEdit /></button>}
          {canDelete && <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(d => 
    d.name?.toLowerCase().includes(filter.toLowerCase()) || 
    d.district_id?.toLowerCase().includes(filter.toLowerCase()) ||
    d.taluka_names?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card">
      <TableToolbar 
        title="Districts"
        subtitle="Manage all district records and their administrative units"
        onSearch={setFilter}
        onAdd={canCreate ? onCreateDistrict : null}
        addText="Add District"
        showImportExport={true}
        onExport={() => districtAPI.exportCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'districts.csv');
          document.body.appendChild(link);
          link.click();
        })}
        onImport={async (file) => {
          const text = await file.text();
          await districtAPI.importCsv(text);
          fetchDistricts();
          Swal.fire('Success', 'Districts imported successfully', 'success');
        }}
        onDownloadSample={() => districtAPI.sampleCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'districts_sample.csv');
          document.body.appendChild(link);
          link.click();
        })}
      />

      <div style={{ padding: '0 30px 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
        />
      </div>

      {showViewModal && selectedDistrict && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>District Details</h3>
              <button onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>ID</th><td>{selectedDistrict.id}</td></tr>
                  <tr><th>Code</th><td>{selectedDistrict.district_id}</td></tr>
                  <tr><th>Name</th><td>{selectedDistrict.name}</td></tr>
                  <tr><th>Talukas</th><td>{selectedDistrict.taluka_names}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showEditModal && selectedDistrict && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Edit District</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <DistrictCreate
                district={selectedDistrict}
                onClose={() => setShowEditModal(false)}
                onSubmit={() => { fetchDistricts(); setShowEditModal(false); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

DistrictTable.propTypes = {
  onCreateDistrict: PropTypes.func,
  reloadKey: PropTypes.number
};

export default DistrictTable;
